import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '../api/client';

/**
 * 「允许选择过去日期（补录模式）」——状态在服务端。
 *
 * 早先这份开关存在 localStorage，后果是它只管得住当前这台浏览器：
 * web 开了 App 照样选不了过去日期、换设备就得重开，而后端一刀不拦，
 * 直接调 `POST /orders` 传任意历史日期都能落库，「24 小时自动关闭防误录」形同虚设。
 * 现在窗口由 `settings` 表按用户存到期时间，服务端在下单与改档期时判定，四端一致，
 * 这里只负责读状态、开关，以及把管理员熔断开给界面看。
 */
export interface OrderBehavior {
  allowPastDates: boolean;
  /** 窗口到期时间（ISO）；未开启为 null */
  expiresAt: string | null;
  remainingMs: number;
  /** 管理员已全局禁止补录：此时用户自己开着也不生效 */
  lockedByAdmin: boolean;
  windowHours: number;
}

/** 取数失败或首帧未回来时按「关闭」处理：闸门默认从严，不给过去日期开临时口子 */
export const ORDER_BEHAVIOR_OFF: OrderBehavior = {
  allowPastDates: false,
  expiresAt: null,
  remainingMs: 0,
  lockedByAdmin: false,
  windowHours: 24,
};

export const ORDER_BEHAVIOR_QUERY_KEY = ['order-behavior'];

export function useOrderBehavior() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ORDER_BEHAVIOR_QUERY_KEY,
    queryFn: async () => (await get<OrderBehavior>('/order-behavior')) ?? ORDER_BEHAVIOR_OFF,
    // 剩余时间会自己走完，开着的时候每分钟和服务端对一次，关闭状态无需轮询
    refetchInterval: (state) => (state.state.data?.allowPastDates ? 60_000 : false),
  });

  const mutation = useMutation({
    mutationFn: async (enabled: boolean) =>
      (await put<OrderBehavior>('/order-behavior', { allowPastDates: enabled })) ?? ORDER_BEHAVIOR_OFF,
    onSuccess: (data) => queryClient.setQueryData(ORDER_BEHAVIOR_QUERY_KEY, data),
  });

  return {
    behavior: query.data ?? ORDER_BEHAVIOR_OFF,
    loading: query.isLoading,
    switching: mutation.isPending,
    switchError: mutation.error,
    setEnabled: mutation.mutate,
    /** 提交被服务端闸门拒绝时调它：本地缓存的那份窗口状态已经不对了 */
    refresh: query.refetch,
  };
}

/** 把剩余毫秒格式化为「X 小时 Y 分钟」 */
export function formatRemaining(ms: number): string {
  if (ms <= 0) return '0 分钟';
  const totalMin = Math.ceil(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h} 小时 ${m} 分钟`;
  if (h > 0) return `${h} 小时`;
  return `${m} 分钟`;
}
